import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import Logo from '../Common/Logo.js';
import { PageSkeleton } from '../Common/SkeletonLoader.js';

const MainLayout = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Simuler indlæsningstid for at vise skeleton
    const timer = setTimeout(() => {
      setLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Base layout
  const Layout = ({ children }) => (
    <div className="min-h-screen flex flex-col">
      {/* Sticky header */}
      <header className="sticky top-0 z-50 bg-white shadow-sm">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex-shrink-0">
              <Link to="/">
                <Logo className="h-12" />
              </Link>
            </div>
            
            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-600 hover:text-brand-dark hover:bg-gray-100"
              >
                <span className="sr-only">Åbn menu</span>
                <svg 
                  className={`h-6 w-6 ${isMobileMenuOpen ? 'hidden' : 'block'}`}
                  stroke="currentColor" 
                  fill="none" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
                <svg 
                  className={`h-6 w-6 ${isMobileMenuOpen ? 'block' : 'hidden'}`}
                  stroke="currentColor" 
                  fill="none" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Desktop navigation */}
            <nav className="hidden md:flex space-x-8">
              <NavLink 
                  to="/funktioner"
                  className={({ isActive }) => 
                      `text-base px-3 py-2 rounded-md text-md font-montserrat font-medium transition-colors duration-1000 ${
                          isActive 
                              ? 'text-brand-dark' 
                              : 'text-gray-950 hover:text-brand-dark'
                      }`
                  }
              >
                  Funktioner
              </NavLink>
              <NavLink 
                  to="/onboarding"
                  className="text-white bg-brand-dark hover:bg-brand-dark/90 px-4 py-2 rounded-md text-md font-montserrat font-medium transition-colors duration-200"
              >
                  Opret hjemmeside
              </NavLink>
          </nav>
          </div>

          {/* Mobile menu */}
          <div className={`${isMobileMenuOpen ? 'block' : 'hidden'} md:hidden py-2`}>
            <div className="space-y-2">
              <NavLink 
                to="/funktioner"
                onClick={() => setIsMobileMenuOpen(false)}
                className={({ isActive }) => 
                  `block px-3 py-2 rounded-md text-base font-medium ${
                    isActive 
                      ? 'text-brand-dark bg-brand-light/20' 
                      : 'text-gray-900 hover:text-brand-dark hover:bg-gray-50'
                  }`
                }
              >
                Funktioner
              </NavLink>
              <NavLink 
                to="/kontakt"
                onClick={() => setIsMobileMenuOpen(false)}
                className={({ isActive }) => 
                  `block px-3 py-2 rounded-md text-base font-medium ${
                    isActive 
                      ? 'text-brand-dark bg-brand-light/20' 
                      : 'text-gray-900 hover:text-brand-dark hover:bg-gray-50'
                  }`
                }
              >
                Kontakt
              </NavLink>
              <NavLink 
                to="/onboarding"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-3 py-2 rounded-md text-base font-medium text-white bg-brand-dark hover:bg-brand-dark/90"
              >
                Opret hjemmeside
              </NavLink>
            </div>
          </div>
        </nav>
      </header>

      {/* Main content */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
                <Link to="/">
                    <Logo className="h-8" darkMode small />
                </Link>
              <p className="mt-4 text-gray-400">
                Skab din professionelle online tilstedeværelse nemt og hurtigt
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold font-montserrat mb-4">Links</h3>
              <ul className="space-y-2">
              <li>
                  <Link to="/om" className="text-gray-400 hover:text-white">
                    Om
                  </Link>
                </li>
                <li>
                  <Link to="/funktioner" className="text-gray-400 hover:text-white">
                    Funktioner
                  </Link>
                </li>
                <li>
                  <Link to="/onboarding" className="text-gray-400 hover:text-white">
                    Opret hjemmeside
                  </Link>
                </li>
                <li>
                  <Link to="/faq" className="text-gray-400 hover:text-white">
                    Ofte stillede spørgsmål
                  </Link>
                </li>
                <li>
                  <Link to="/hvorfor-hjemmeside-haandvaerker" className="text-gray-400 hover:text-white">
                    Få flere kunder med en simpel hjemmeside
                  </Link>
                </li>
                <li>
                  <Link to="/blog" className="text-gray-400 hover:text-white">
                    Blog
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold font-montserrat mb-4">Kontakt</h3>
              <p className="text-gray-400">
                Email: <a href="mailto:kontakt@haandvaerkernet.dk">kontakt@haandvaerkernet.dk</a>
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold font-montserrat mb-4">Fagspecifikke sider</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/tomrer-hjemmeside" className="text-gray-400 hover:text-white">
                    Tømrer hjemmeside
                  </Link>
                </li>
                <li>
                  <Link to="/murer-hjemmeside" className="text-gray-400 hover:text-white">
                    Murer hjemmeside
                  </Link>
                </li>
                <li>
                  <Link to="/vvs-hjemmeside" className="text-gray-400 hover:text-white">
                    VVS hjemmeside
                  </Link>
                </li>
                <li>
                  <Link to="/maler-hjemmeside" className="text-gray-400 hover:text-white">
                    Maler hjemmeside
                  </Link>
                </li>
                <li>
                  <Link to="/vinduespudser-hjemmeside" className="text-gray-400 hover:text-white">
                    Vinduespudser hjemmeside
                  </Link>
                </li>
                <li>
                  <Link to="/mekaniker-hjemmeside" className="text-gray-400 hover:text-white">
                    Mekaniker hjemmeside
                  </Link>
                </li>
                <li>
                  <Link to="/elektriker-hjemmeside" className="text-gray-400 hover:text-white">
                    Elektriker hjemmeside
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );

  return loading ? (
    <Layout>
      <PageSkeleton type="default" />
    </Layout>
  ) : (
    <Layout>
      {children}
    </Layout>
  );
};

export default MainLayout;